import React, {useCallback, useMemo, useRef, useState} from 'react';

import {safeLocalStorage, safeSessionStorage} from '../../../util/localStorage';
import {type Account} from '../SearchNav/types';
import {
  AccountSelectorContext,
  AccountSelectorStorageKeys,
} from './AccountSelectorContextProvider';
import {
  getAccountFromStorage,
  getCanSeeOrgInURL,
  getSelectedAccount,
} from './AccountSelectorUtils';
import {useUrlAccountInputs} from './useUrlAccountInputs';

export const AccountSelectorContextProviderWrapper: React.FC<{
  children?: React.ReactNode;
}> = ({children}) => {
  const {
    viewer,
    accountList,
    isAccountListLoading,
    userInfo,
    orgName,
    entityName,
    orgQuery,
    entityQuery,
    personalEntityUserID,
    isAdminMode,
    isProfilePath,
    isLoaded,
  } = useUrlAccountInputs();

  // Track the selected account in React state so context consumers
  // re-render when the account changes. This replaces the dynamic `key`
  // prop on AccountSelectorLoadedProvider that previously caused full
  // subtree remounts.
  const [selectedAccount, setSelectedAccountState] = useState<Account | null>(
    null
  );

  const updateSelectedAccount = useCallback(
    (value: Account | null) => {
      const stringValue = JSON.stringify(value);
      safeSessionStorage.setItem(
        AccountSelectorStorageKeys.ACCOUNT_SELECTOR_VALUE,
        stringValue
      );
      safeLocalStorage.setItem(
        AccountSelectorStorageKeys.LAST_ACCOUNT_SELECTOR_VALUE,
        stringValue
      );
      setSelectedAccountState(value);
    },
    [setSelectedAccountState]
  );

  const updateLastNonImpersonatedAccount = useCallback(
    (value: Account | null) => {
      const stringValue = JSON.stringify(value);
      safeLocalStorage.setItem(
        AccountSelectorStorageKeys.LAST_NON_IMPERSONATED_ACCOUNT,
        stringValue
      );
    },
    []
  );

  // One-time initialization: when loading completes, compute the correct
  // selected account and sync it to session/local storage + React state.
  // Uses a ref to track the last session-storage account ID we initialized
  // against, so that manual account switches trigger re-initialization.
  const lastInitializedForRef = useRef<string | undefined>(undefined);

  if (isLoaded && viewer != null) {
    const sessionStorageAccount = getAccountFromStorage(
      safeSessionStorage,
      AccountSelectorStorageKeys.ACCOUNT_SELECTOR_VALUE,
      accountList
    );
    const sessionKey = sessionStorageAccount?.id ?? 'none';

    if (lastInitializedForRef.current !== sessionKey) {
      lastInitializedForRef.current = sessionKey;

      const computedAccount = getSelectedAccount({
        selectedAccount: sessionStorageAccount,
        lastSelectedAccount: getAccountFromStorage(
          safeLocalStorage,
          AccountSelectorStorageKeys.LAST_ACCOUNT_SELECTOR_VALUE,
          accountList
        ),
        isProfilePath,
        isAdminMode,
        viewer,
        accountList,
        userInfo,
        orgName,
        entityName,
        orgQuery,
        entityQuery,
        personalEntityUserID,
      });

      if (
        JSON.stringify(sessionStorageAccount) !==
        JSON.stringify(computedAccount)
      ) {
        const stringValue = JSON.stringify(computedAccount);
        safeSessionStorage.setItem(
          AccountSelectorStorageKeys.ACCOUNT_SELECTOR_VALUE,
          stringValue
        );
        safeLocalStorage.setItem(
          AccountSelectorStorageKeys.LAST_ACCOUNT_SELECTOR_VALUE,
          stringValue
        );
        setSelectedAccountState(computedAccount);
      } else if (
        JSON.stringify(selectedAccount) !==
        JSON.stringify(sessionStorageAccount)
      ) {
        setSelectedAccountState(sessionStorageAccount);
      }
    }
  }

  const lastNonImpersonatedAccount = isLoaded
    ? getAccountFromStorage(
        safeLocalStorage,
        AccountSelectorStorageKeys.LAST_NON_IMPERSONATED_ACCOUNT,
        accountList
      )
    : null;

  const canSeeOrgInURL = isLoaded
    ? getCanSeeOrgInURL({
        selectedAccount,
        lastSelectedAccount: getAccountFromStorage(
          safeLocalStorage,
          AccountSelectorStorageKeys.LAST_ACCOUNT_SELECTOR_VALUE,
          accountList
        ),
        isProfilePath,
        isAdminMode,
        viewer: viewer!,
        accountList,
        userInfo,
        orgName,
        entityName,
        orgQuery,
        entityQuery,
      })
    : true;

  // Always render the same tree structure to avoid remounting children.
  const value = useMemo(
    () => ({
      selectedAccount: isLoaded ? selectedAccount : null,
      setSelectedAccount: updateSelectedAccount,
      lastNonImpersonatedAccount: isLoaded ? lastNonImpersonatedAccount : null,
      setLastNonImpersonatedAccount: updateLastNonImpersonatedAccount,
      canSeeOrgInURL,
      accountList,
      isAccountListLoading,
    }),
    [
      isLoaded,
      selectedAccount,
      updateSelectedAccount,
      lastNonImpersonatedAccount,
      updateLastNonImpersonatedAccount,
      canSeeOrgInURL,
      accountList,
      isAccountListLoading,
    ]
  );

  return (
    <AccountSelectorContext.Provider value={value}>
      {children}
    </AccountSelectorContext.Provider>
  );
};
